import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Link2, Copy, Ban, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { pillarApi, pillarErr, pillarDate, label } from "@/lib/pillarApi";
import { inputCls, labelCls, GoldButton, GhostButton, Pill, ErrorLine } from "@/components/nds/windows/resident/ui";
import { STATUS_TONE } from "./PillarProgress";
import { AIReview } from "./AIReview";

export function MagicLink({ residentId }) {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState("");
  const load = () => pillarApi.get(`/residents/${residentId}/portal-token`).then(setStatus).catch(() => {});
  useEffect(() => { load(); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  const issue = async () => {
    const d = await pillarApi.post(`/residents/${residentId}/portal-token`);
    setLink(`${window.location.origin}${d.path}`); load();
  };
  const revoke = async () => { await pillarApi.del(`/residents/${residentId}/portal-token`); setLink(""); load(); };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Copy failed"); } };
  return (
    <div data-testid="pillar-magic-link" className="p-3 border border-white/10 rounded-sm space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Link2 className="h-3.5 w-3.5 text-nex-gold" />
        <span className={labelCls + " !mb-0"}>Resident portal link</span>
        <Pill tone={status?.active ? "green" : "dim"} data-testid="pillar-magic-link-status">{status?.active ? "active" : "none"}</Pill>
        <div className="ml-auto flex gap-2">
          <GoldButton data-testid="pillar-magic-link-generate" onClick={issue}>{status?.active ? "Issue new link" : "Generate link"}</GoldButton>
          {status?.active && <GhostButton data-testid="pillar-magic-link-revoke" onClick={revoke}><Ban className="h-3 w-3" /> Revoke</GhostButton>}
        </div>
      </div>
      {link && (
        <div className="flex gap-2">
          <input data-testid="pillar-magic-link-url" readOnly value={link} className={inputCls + " font-mono text-xs"} />
          <GhostButton data-testid="pillar-magic-link-copy" onClick={copy}><Copy className="h-3 w-3" /> Copy</GhostButton>
        </div>
      )}
      <p className="text-[11px] text-nex-muted">Links work until revoked. Issuing a new link revokes the old one.</p>
    </div>
  );
}

export function NewAssignment({ residentId, pillarId, onCreated }) {
  const [week, setWeek] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const create = async () => {
    setBusy(true); setError(null);
    try {
      await pillarApi.post(`/residents/${residentId}/pillar-assignments`, { pillar_id: pillarId, ...(week ? { program_week: Number(week) } : {}) });
      setWeek(""); toast.success("Assignment created"); onCreated();
    } catch (e) { setError(pillarErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 border border-white/10 rounded-sm space-y-2">
      <div className="flex gap-2">
        <input data-testid="pillar-new-week" type="number" min={1} value={week} placeholder="Next week (auto)" onChange={(e) => setWeek(e.target.value)} className={inputCls + " !w-40"} />
        <GoldButton data-testid="pillar-new-create" onClick={create} disabled={busy} className="ml-auto">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} New assignment
        </GoldButton>
      </div>
      <ErrorLine testId="pillar-new-error">{error}</ErrorLine>
    </div>
  );
}

function StaffResponse({ assignment, onDone }) {
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const edited = useRef(false);
  const onDraft = useCallback((t, force) => { if (force || !edited.current) { setText(t); edited.current = false; } }, []);
  const n = text.trim().length;
  const send = async () => {
    setBusy(true); setError(null);
    try {
      const r = await pillarApi.post(`/pillar-assignments/${assignment.id}/staff-response`, { response: text });
      toast.success(r.phase_advanced ? `Verified · advanced to Phase ${r.phase_advanced.to_phase}` : "Verified · payout posted");
      onDone();
    } catch (e) { setError(pillarErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2">
      <AIReview assignment={assignment} onDraft={onDraft} />
      <textarea data-testid={`pillar-staff-response-${assignment.id}`} rows={text.length > 300 ? 10 : 3} value={text} onChange={(e) => { edited.current = true; setText(e.target.value); }}
        placeholder="Staff assessment (min 250 characters)… The AI draft appears here; edit it before you send." className={inputCls} />
      <div className="flex items-center gap-2">
        <span className={`font-mono text-[10px] ${n >= 250 ? "text-emerald-300" : "text-nex-muted"}`}>{n}/250</span>
        <GoldButton data-testid={`pillar-staff-verify-${assignment.id}`} onClick={send} disabled={busy || n < 250} className="ml-auto">
          <CheckCircle2 className="h-3 w-3" /> Verify & send to resident
        </GoldButton>
      </div>
      <ErrorLine testId={`pillar-staff-error-${assignment.id}`}>{error}</ErrorLine>
    </div>
  );
}

export function AssignmentCard({ a, hw, onChanged }) {
  return (
    <li data-testid={`pillar-assignment-${a.id}`} className="p-3 border border-white/5 bg-white/[0.02] rounded-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="dim">Week {a.program_week}</Pill>
        <Pill tone={STATUS_TONE[a.status]} data-testid={`pillar-assignment-status-${a.id}`}>{label(a.status)}</Pill>
        {hw?.urgent && <Pill tone="red"><AlertTriangle className="h-3 w-3 mr-1" />urgent</Pill>}
        <span className="ml-auto text-[11px] text-nex-muted">{pillarDate(a.assigned_at)}</span>
      </div>
      <p className="mt-2 text-sm text-nex-silver/90">{a.node_snapshot?.question_text}</p>
      {a.selected_option && (
        <div className="mt-2 text-xs text-nex-muted">
          <span className="text-nex-gold">{a.selected_option.letter}.</span> {a.selected_option.text}
          <p className="mt-1 italic text-nex-silver/70">“{a.resident_reason}”</p>
        </div>
      )}
      {a.status === "resident_submitted" && <StaffResponse assignment={a} onDone={onChanged} />}
      {a.staff_response && <p className="mt-2 text-xs text-emerald-200/80">Staff: {a.staff_response}</p>}
    </li>
  );
}
